import { GAMEPLAY_RULES } from "./generatedGameplayRules.js";
import { CANDIDATE_MINING_RULES } from "./MiningEconomy.js";
export const SCANNER_TIERS = ["COMMON", "UNCOMMON", "RARE", "EPIC", "LEGENDARY"];
export const ACTIVE_SCANNER_BASE_LAMPORTS = Object.freeze({
    COMMON: 1000000n, UNCOMMON: 5000000n, RARE: 25000000n, EPIC: 100000000n, LEGENDARY: 200000000n,
});
export const SCANNER_REFINING_MEAN = 0.97;
export const SCANNER_FUEL_USDC = 0.1;
export const SCANNER_MODEL_VERSION = CANDIDATE_MINING_RULES.version;
/** Pinned comparison inputs from read-only devnet RPC. Actual approvals read fresh message fees and rent. */
export const MINING_PAYMENT_COST_SCENARIO = Object.freeze({
    observedAt: "2026-10-07T03:19:51.987Z", network: "SOLANA_DEVNET",
    admissionNetworkFeeLamports: "25300", scannerNetworkFeeLamports: "30300",
    admissionAccountRentLamports: "1402080", scannerAccountRentLamports: "1605280",
});
export const MINING_CHECKOUT_NETWORK_SOL = (Number(MINING_PAYMENT_COST_SCENARIO.admissionNetworkFeeLamports) + Number(MINING_PAYMENT_COST_SCENARIO.scannerNetworkFeeLamports)) / 1e9;
export const MINING_CHECKOUT_ACCOUNT_SOL = (Number(MINING_PAYMENT_COST_SCENARIO.admissionAccountRentLamports) + Number(MINING_PAYMENT_COST_SCENARIO.scannerAccountRentLamports)) / 1e9;
/** Reviewable scenario only. Neither payment nor reward code imports this preset. */
export const PROPOSED_SCANNER_TIERS = Object.freeze(SCANNER_TIERS.map((tier, index) => Object.freeze({
    tier, baseScannerSol: 0.001 * 1.5 ** index, meanRaw: 12 / 2 ** index,
    baseVpPerMaterial: 2 ** index, dailyProcessedTarget: 1200 / 2 ** index,
})));
export function activeScannerTier(tier, vpPerMaterial) {
    const rules = GAMEPLAY_RULES.tiers.find(row => row.id === tier);
    return { tier, baseScannerSol: Number(ACTIVE_SCANNER_BASE_LAMPORTS[tier]) / 1e9,
        meanRaw: Number(GAMEPLAY_RULES.core.targetedRawPerCore[tier]) / 1e6 * 1.2,
        baseVpPerMaterial: vpPerMaterial, dailyProcessedTarget: Number(rules.dailyProcessedTarget) };
}
export const DEFAULT_SCANNER_SCENARIO = Object.freeze({
    difficulty: 1, priceExponent: 0.5, yieldExponent: 1, solUsd: 100,
    cubeCount: 1, vpBonus: 1, materialSaleUsd: null, saleFeeBps: 0,
});
function between(value, min, max) { return Number.isFinite(value) && value >= min && value <= max; }
/** Long-run expected values, not a payout quote, market-price floor or ROI forecast. */
export function modelScanner(tier, input) {
    if (!SCANNER_TIERS.includes(tier.tier) || !between(tier.baseScannerSol, 0.000000001, 1e6)
        || !between(tier.meanRaw, 0.000001, 1e9) || !between(tier.baseVpPerMaterial, 1, 1e6)
        || !between(tier.dailyProcessedTarget, 0.000001, 1e12)
        || !between(input.difficulty, 0.01, 1000) || !between(input.priceExponent, 0, 2) || !between(input.yieldExponent, 0, 2)
        || !between(input.solUsd, 0.000001, 1e6) || !Number.isSafeInteger(input.cubeCount) || !between(input.cubeCount, 1, 4096)
        || !between(input.vpBonus, 1, 100) || !between(input.saleFeeBps, 0, 10_000)
        || input.materialSaleUsd !== null && !between(input.materialSaleUsd, 0, 1e12))
        throw new RangeError("scanner_model_input_invalid");
    const difficulty = Math.max(1, input.difficulty);
    let totalScannerSol = 0, totalRaw = 0, lastDifficulty = difficulty;
    for (let cube = 0; cube < input.cubeCount; cube++) {
        lastDifficulty = Math.min(1000, difficulty + totalRaw / (tier.dailyProcessedTarget / SCANNER_REFINING_MEAN));
        totalScannerSol += Math.ceil(Math.round(tier.baseScannerSol * 1e9) * lastDifficulty ** input.priceExponent) / 1e9;
        totalRaw += Math.max(0.000001, Math.floor(tier.meanRaw * 1e6 / lastDifficulty ** input.yieldExponent) / 1e6);
    }
    const scannerSol = totalScannerSol / input.cubeCount;
    const meanRaw = totalRaw / input.cubeCount;
    const meanMaterial = meanRaw * SCANNER_REFINING_MEAN;
    const vpPerMaterial = tier.baseVpPerMaterial * input.vpBonus;
    const meanVp = meanMaterial * vpPerMaterial;
    const scannerUsd = scannerSol * input.solUsd;
    const fee = input.networkFeeUsd ?? MINING_CHECKOUT_NETWORK_SOL * input.solUsd, funding = (input.accountFundingSol ?? MINING_CHECKOUT_ACCOUNT_SOL) * input.solUsd;
    const liquidity = input.marketLiquidityUsd ?? 1000, pool = input.rewardPoolUsd ?? 0, competingVp = input.competingVp ?? 0;
    if (![fee, funding, liquidity, pool, competingVp].every(value => between(value, 0, 1e15)) || liquidity <= 0)
        throw new RangeError("scanner_model_input_invalid");
    const spendUsd = scannerUsd + SCANNER_FUEL_USDC + (fee + funding) / input.cubeCount;
    // Finite constant-product market: sell into the existing USD side; never use
    // a fixed quote for unlimited volume. Selling and contributing are alternatives.
    const grossSale = input.materialSaleUsd === null ? null : meanMaterial * input.cubeCount * input.materialSaleUsd * (1 - input.saleFeeBps / 10_000);
    const marketOutputUsd = grossSale === null ? null : liquidity * grossSale / (liquidity + grossSale);
    const netSaleFuel = marketOutputUsd === null ? null : marketOutputUsd / SCANNER_FUEL_USDC * .995;
    const materialSaleUsd = netSaleFuel === null ? null : netSaleFuel * SCANNER_FUEL_USDC * .95;
    return Object.freeze({
        tier: tier.tier, difficulty, lastDifficulty, scannerSol, scannerUsd, spendUsd, meanRaw, meanMaterial, meanVp, vpPerMaterial,
        costPerMaterialUsd: spendUsd / meanMaterial, costPerVpUsd: spendUsd / meanVp,
        costPerRawUsd: spendUsd / meanRaw,
        scannerOnlyCostPerVpSol: scannerSol / meanVp,
        totalSpendUsd: spendUsd * input.cubeCount, totalRaw: meanRaw * input.cubeCount,
        totalNetworkFeeUsd: fee, totalAccountFundingUsd: funding,
        totalMaterial: meanMaterial * input.cubeCount, totalVp: meanVp * input.cubeCount,
        dailyTarget: tier.dailyProcessedTarget, cubesAtTarget: tier.dailyProcessedTarget / meanMaterial,
        // Accounting allocation, before fees/service cost. These are not profit.
        treasuryUsd: (scannerUsd * 0.2 + 0.006) * input.cubeCount,
        crewPoolUsd: scannerUsd * 0.7 * input.cubeCount,
        operationsUsd: (scannerUsd * 0.1 + 0.001) * input.cubeCount,
        playerPoolUsd: 0.09 * input.cubeCount,
        crewGrowthOrBuybackUsd: 0.003 * input.cubeCount,
        materialSaleUsd, netSaleFuel, recycledCubeCoverage: netSaleFuel,
        saleSurplusUsd: materialSaleUsd === null ? null : materialSaleUsd - spendUsd * input.cubeCount,
        salePriceImpactPercent: grossSale ? (1 - marketOutputUsd / grossSale) * 100 : 0,
        contributionRewardShareUsd: pool * meanVp * input.cubeCount / Math.max(1, meanVp * input.cubeCount + competingVp),
    });
}
/** Active-rule disclosure shared by the catalog and admin. No guessed market value. */
export function scannerConditions(tier, difficultyMillionths, processed = null, observedWindowHours = 24) {
    if (difficultyMillionths < BigInt(GAMEPLAY_RULES.emission.minimumDifficulty)
        || difficultyMillionths > BigInt(GAMEPLAY_RULES.emission.maximumDifficulty)
        || processed !== null && (processed < 0n || processed > 0xffffffffffffffffn)
        || !Number.isInteger(observedWindowHours) || observedWindowHours < 1 || observedWindowHours > 24)
        throw new RangeError("scanner_conditions_invalid");
    const difficulty = difficultyMillionths < 1000000n ? 1000000n : difficultyMillionths;
    const rawPerFind = BigInt(GAMEPLAY_RULES.core.targetedRawPerCore[tier]);
    const expectedRaw = rawPerFind * 6n * 1000000n / (5n * difficulty);
    return {
        expectedRawBaseUnits: expectedRaw.toString(),
        minimumRawBaseUnits: (rawPerFind * 8500n * 1000000n / (10000n * difficulty)).toString(),
        maximumRawBaseUnits: (rawPerFind * 3n * 11500n * 1000000n / (10000n * difficulty)).toString(),
        expectedMaterialBaseUnits: (expectedRaw * 97n / 100n).toString(),
        dailyProcessedTargetBaseUnits: (BigInt(GAMEPLAY_RULES.tiers.find(row => row.id === tier).dailyProcessedTarget) * 1000000n).toString(),
        trailing24HourProcessedBaseUnits: processed?.toString() ?? null,
        observedWindowHours, hardDailyLimitBaseUnits: null,
    };
}
